// client/src/pages/Splash/Splash.jsx
import React, {
  useContext,
  useEffect,
  useLayoutEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import { Link } from "react-router-dom";
import { AuthContext } from "../../context/AuthContext";
import "./Splash.css";

/* Parts drawing used as the hero background. Served from public/ rather than
   imported so it can be swapped without a rebuild, which is how the kiosk
   already references its images. Passed to CSS as a custom property because
   css-loader tries to resolve a public path in url() as a module. */
const BLUEPRINT_IMAGE = `${process.env.PUBLIC_URL}/static/imgs/shop/engineering-outline.png`;

/* Photography slot.
   Drop a landscape shot of the shop floor into
   client/public/static/imgs/shop/ and fill in src + alt below. Leave src
   empty and the hero renders a designed icon panel instead, so the page is
   finished either way. Target 1920x1440 (4:3), progressive JPEG, under 400KB. */
const HERO_PHOTO = {
  src: "",
  alt: "",
  width: 1920,
  height: 1440,
};

const HERO_FACTS = [
  {
    icon: "fa-clock",
    head: "Monday to Friday",
    body: "9:00 AM – 5:00 PM during the fall semester",
  },
  {
    icon: "fa-map-marker-alt",
    head: "Engineering Building",
    body: "GCU main campus",
  },
  {
    icon: "fa-tools",
    head: "Tool checkout",
    body: "Machine access, staffed during posted hours",
  },
];

const FALL_HOURS = [
  { rows: [{ label: "Monday to Friday", range: "9:00 AM – 5:00 PM" }] },
  {
    group: "Capstone hours",
    rows: [{ label: "Monday to Friday", range: "9:00 AM – 5:00 PM" }],
  },
];

const SPRING_HOURS = [
  {
    rows: [
      { label: "Monday to Friday", range: "9:00 AM – 10:00 PM" },
      { label: "Selected Saturdays", range: "10:00 AM – 3:00 PM" },
    ],
  },
  {
    group: "Capstone hours",
    rows: [
      { label: "Monday to Friday", range: "9:00 AM – 10:00 PM" },
      { label: "Selected Saturdays", range: "10:00 AM – 3:00 PM" },
    ],
  },
];

/* Font Awesome 5.15.4 is loaded globally in public/index.html. Every name
   here exists in FA5 free-solid; FA6-only names (fa-shirt, fa-person,
   fa-helmet-safety, fa-rotate) render as a blank glyph under 5.x. */
const REQUIREMENTS = [
  { icon: "fa-user-alt", label: "Hair up and back (if applicable)" },
  { icon: "fa-glasses", label: "Safety glasses" },
  { icon: "pants", label: "Pants" },
  { icon: "fa-shoe-prints", label: "Closed-toe shoes" },
];

const CLEANUP_NOTE =
  "Cleanup starts 30 minutes before close. No projects left overnight.";

/* Kept as a function, not a frozen literal: it reads window.innerWidth, so a
   module-scope constant would pin the particle count to the first page load. */
const buildParticleConfig = () => {
  const isMobile = window.innerWidth <= 768;

  return {
    particles: {
      number: {
        value: isMobile ? 30 : 50,
        density: { enable: true, value_area: 800 },
      },
      color: { value: "#8a4af3" },
      shape: {
        type: "circle",
        stroke: { width: 0, color: "#8a4af3" },
        polygon: { nb_sides: 5 },
      },
      opacity: {
        value: 0.35,
        random: false,
        anim: { enable: false, speed: 1, opacity_min: 0.1, sync: false },
      },
      size: {
        value: 3,
        random: true,
        anim: { enable: false, speed: 40, size_min: 0.1, sync: false },
      },
      line_linked: {
        enable: true,
        distance: 120,
        color: "#8a4af3",
        opacity: 0.22,
        width: 1,
      },
      move: {
        enable: true,
        speed: 1.6,
        direction: "none",
        random: false,
        straight: false,
        out_mode: "out",
        bounce: false,
        attract: { enable: false, rotateX: 600, rotateY: 1200 },
      },
    },
    interactivity: {
      detect_on: "canvas",
      events: {
        onhover: { enable: true, mode: "repulse" },
        onclick: { enable: true, mode: "push" },
        resize: true,
      },
      modes: {
        grab: { distance: 140, line_linked: { opacity: 1 } },
        bubble: { distance: 400, size: 40, duration: 2, opacity: 8, speed: 3 },
        repulse: { distance: 150, duration: 0.4 },
        push: { particles_nb: 4 },
        remove: { particles_nb: 2 },
      },
    },
    retina_detect: true,
  };
};

const HoursBlock = ({ title, groups, note, reveal }) => (
  <div className="hours-block" data-reveal={reveal ? "" : undefined}>
    <h3 className="hours-block-title">{title}</h3>
    {groups.map((group, index) => (
      <div className="hours-group" key={group.group || `group-${index}`}>
        {group.group && (
          <p className="hours-group-label">
            <i className="fas fa-graduation-cap" aria-hidden="true"></i>
            {group.group}
          </p>
        )}
        <dl className="hours-rows">
          {group.rows.map((row) => (
            <div className="hours-row" key={`${row.label}-${row.range}`}>
              <dt>{row.label}</dt>
              <dd>{row.range}</dd>
            </div>
          ))}
        </dl>
      </div>
    ))}
    {note && <p className="hours-note">{note}</p>}
  </div>
);

const Splash = () => {
  const { isAuthenticated } = useContext(AuthContext);
  const pageRef = useRef(null);
  const saturdayStorageKey = "gcuengshop.saturdayDatesContent";
  const [isEditingSaturday, setIsEditingSaturday] = useState(false);
  const defaultSaturdayData = useMemo(
    () => ({
      title: "Spring Semester Saturday Dates",
      dates: ["Jan31st", "Feb21st", "Feb28th", "Mar21st", "Mar28th", "Apr11th"],
      hoursLabel: "Saturday hours",
      hoursRange: "10:00 AM – 3:00 PM",
      note: "Cleanup starts 30 minutes before close. No projects left overnight.",
    }),
    []
  );
  const [saturdayData, setSaturdayData] = useState(defaultSaturdayData);
  const [saturdayDraft, setSaturdayDraft] = useState(defaultSaturdayData);

  useEffect(() => {
    const prefersReducedMotion =
      window.matchMedia &&
      window.matchMedia("(prefers-reduced-motion: reduce)").matches;
    if (prefersReducedMotion) {
      return undefined;
    }

    let cancelled = false;

    const initializeParticles = () => {
      if (cancelled || !window.particlesJS) {
        return;
      }
      window.particlesJS("particles-js", buildParticleConfig());
    };

    if (window.particlesJS) {
      initializeParticles();
    } else {
      const script = document.createElement("script");
      script.src =
        "https://cdn.jsdelivr.net/npm/particles.js@2.0.0/particles.min.js";
      script.async = true;
      script.onload = initializeParticles;
      document.body.appendChild(script);
    }

    return () => {
      cancelled = true;
      // particles.js owns its own rAF loop and canvas. destroypJS stops both;
      // without it the loop keeps running after the page unmounts.
      (window.pJSDom || []).forEach((instance) => {
        try {
          instance.pJS.fn.vendors.destroypJS();
        } catch (error) {
          /* already torn down */
        }
      });
      window.pJSDom = [];
    };
  }, []);

  // Reveal-on-scroll. useLayoutEffect so elements are armed before the browser
  // paints; arming them in a normal effect shows them first and then hides
  // them, which flashes. Nothing is hidden in CSS until this adds the gate
  // class, so if the script never runs the page is simply all visible.
  useLayoutEffect(() => {
    const page = pageRef.current;
    if (!page) return undefined;

    const targets = Array.from(page.querySelectorAll("[data-reveal]"));
    if (!targets.length) return undefined;

    const show = (el) => el.setAttribute("data-reveal", "in");
    const reduceMotion =
      window.matchMedia &&
      window.matchMedia("(prefers-reduced-motion: reduce)").matches;

    if (reduceMotion || typeof IntersectionObserver === "undefined") {
      targets.forEach(show);
      return undefined;
    }

    page.classList.add("reveal-armed");

    // Measuring here to pre-reveal what is already on screen does not work:
    // this runs before the webfonts load, so the layout is the more compact
    // fallback one and elements read as being higher up the page than they end
    // up. That marked below-fold sections as already seen. The observer is
    // live and recalculates after the font reflow, so it is left to decide,
    // and whatever is in view at load simply fades in once.
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (!entry.isIntersecting) return;
          show(entry.target);
          // One-way: content must not fade back out while it is being read.
          observer.unobserve(entry.target);
        });
      },
      { rootMargin: "0px 0px -10% 0px", threshold: 0.15 }
    );

    targets.forEach((el) => observer.observe(el));

    // Failsafe. Hiding content until an observer says otherwise means any
    // failure to deliver callbacks leaves the hours and the safety
    // requirements permanently invisible, which is far worse than having no
    // animation. Browsers do not run observers for a tab that is not being
    // rendered, so a page opened in a background tab legitimately sits with
    // nothing revealed; that resolves itself on becoming visible, but if
    // nothing has been revealed a couple of seconds after the page is
    // actually on screen, the effect is abandoned and everything shown.
    let disarm = window.setTimeout(function check() {
      if (document.hidden) {
        disarm = window.setTimeout(check, 2000);
        return;
      }
      if (!page.querySelector('[data-reveal="in"]')) {
        page.classList.remove("reveal-armed");
      }
    }, 2000);

    return () => {
      window.clearTimeout(disarm);
      observer.disconnect();
    };
  }, []);

  useEffect(() => {
    const savedContent = localStorage.getItem(saturdayStorageKey);
    if (savedContent) {
      try {
        const parsed = JSON.parse(savedContent);
        setSaturdayData({ ...defaultSaturdayData, ...parsed });
        setSaturdayDraft({ ...defaultSaturdayData, ...parsed });
      } catch (error) {
        setSaturdayData(defaultSaturdayData);
        setSaturdayDraft(defaultSaturdayData);
      }
    }
  }, [defaultSaturdayData, saturdayStorageKey]);

  const toggleSaturdayEdit = () => {
    if (!isAuthenticated) {
      return;
    }
    setIsEditingSaturday((prev) => !prev);
    setSaturdayDraft(saturdayData);
  };

  const handleSaturdayChange = (field, value) => {
    setSaturdayDraft((prev) => ({ ...prev, [field]: value }));
  };

  const handleSaturdayDatesChange = (value) => {
    const dates = value
      .split("\n")
      .map((line) => line.trim())
      .filter(Boolean);
    setSaturdayDraft((prev) => ({ ...prev, dates }));
  };

  const handleSaturdaySave = () => {
    if (!isAuthenticated) {
      return;
    }
    setSaturdayData(saturdayDraft);
    localStorage.setItem(saturdayStorageKey, JSON.stringify(saturdayDraft));
    setIsEditingSaturday(false);
  };

  const handleSaturdayCancel = () => {
    setSaturdayDraft(saturdayData);
    setIsEditingSaturday(false);
  };

  return (
    <div className="splash-page" ref={pageRef}>
      <main className="splash-main">
        <section
          className="splash-band splash-hero"
          aria-labelledby="splash-title"
          style={{ "--hero-art": `url(${BLUEPRINT_IMAGE})` }}
        >
          <div id="particles-js" aria-hidden="true"></div>
          <div className="band-inner">
            <div className={`hero-grid${HERO_PHOTO.src ? " has-photo" : ""}`}>
              <div className="hero-copy">
                <h1 id="splash-title" className="splash-title">
                  GCU Engineering Shop
                </h1>
                <p className="splash-lede">
                  Tool checkout, machine access, and staffed help for
                  engineering students. Hours and shop requirements are below.
                </p>
                {!isAuthenticated && (
                  <div className="cta-row">
                    <Link to="/admin/login" className="cta primary">
                      Admin Login
                    </Link>
                    <Link to="/user/login" className="cta ghost">
                      Student Portal
                    </Link>
                  </div>
                )}
                <ul className="hero-facts">
                  {HERO_FACTS.map((fact) => (
                    <li className="hero-fact" key={fact.head}>
                      <i className={`fas ${fact.icon}`} aria-hidden="true"></i>
                      <span className="hero-fact-head">{fact.head}</span>
                      <span className="hero-fact-body">{fact.body}</span>
                    </li>
                  ))}
                </ul>
              </div>

              {HERO_PHOTO.src && (
                <figure className="photo-figure">
                  <div className="photo-slot">
                    <img
                      className="photo-slot-img"
                      src={HERO_PHOTO.src}
                      alt={HERO_PHOTO.alt}
                      width={HERO_PHOTO.width}
                      height={HERO_PHOTO.height}
                      decoding="async"
                    />
                  </div>
                  <figcaption>Shop floor</figcaption>
                </figure>
              )}
            </div>
          </div>
        </section>

        <section
          className="splash-band hours-section"
          aria-labelledby="hours-heading"
          style={{ "--hero-art": `url(${BLUEPRINT_IMAGE})` }}
        >
          <div className="band-inner">
            <div className="section-head" data-reveal="">
              <h2 id="hours-heading" className="section-title">
                Shop hours
              </h2>
              <p className="section-note">
                Hours change by semester. Saturday dates are kept current by
                shop staff.
              </p>
            </div>

            <div className="hours-grid">
              <HoursBlock
                reveal
                title="Fall Semester"
                groups={FALL_HOURS}
                note="Closed on weekends and holidays."
              />
              <HoursBlock
                reveal
                title="Spring Semester"
                groups={SPRING_HOURS}
                note="Closed on holidays."
              />
            </div>

            <p className="hours-footnote" data-reveal="">
              <i className="fas fa-broom" aria-hidden="true"></i>
              <span>{CLEANUP_NOTE}</span>
            </p>

            <div
              className={`saturday-rail${isEditingSaturday ? " editing" : ""}`}
              data-reveal=""
            >
              <div className="rail-head">
                <h3 className="rail-title">{saturdayData.title}</h3>
                {isAuthenticated && (
                  <button
                    className="edit-pill"
                    type="button"
                    onClick={toggleSaturdayEdit}
                  >
                    {isEditingSaturday ? "Close" : "Edit"}
                  </button>
                )}
              </div>

              <ul className="date-grid">
                {saturdayData.dates.map((date) => (
                  <li key={date} className="date-chip">
                    {date}
                  </li>
                ))}
              </ul>
              <dl className="hours-rows rail-hours">
                <div className="hours-row">
                  <dt>{saturdayData.hoursLabel}</dt>
                  <dd>{saturdayData.hoursRange}</dd>
                </div>
              </dl>

              {saturdayData.note && saturdayData.note !== CLEANUP_NOTE && (
                <p className="hours-note">{saturdayData.note}</p>
              )}

              {isEditingSaturday && (
                <div className="edit-panel">
                  <label className="edit-field">
                    <span>Title</span>
                    <input
                      type="text"
                      value={saturdayDraft.title}
                      onChange={(event) =>
                        handleSaturdayChange("title", event.target.value)
                      }
                    />
                  </label>
                  <label className="edit-field">
                    <span>Dates (one per line)</span>
                    <textarea
                      rows={5}
                      value={saturdayDraft.dates.join("\n")}
                      onChange={(event) =>
                        handleSaturdayDatesChange(event.target.value)
                      }
                    />
                  </label>
                  <label className="edit-field">
                    <span>Hours label</span>
                    <input
                      type="text"
                      value={saturdayDraft.hoursLabel}
                      onChange={(event) =>
                        handleSaturdayChange("hoursLabel", event.target.value)
                      }
                    />
                  </label>
                  <label className="edit-field">
                    <span>Hours range</span>
                    <input
                      type="text"
                      value={saturdayDraft.hoursRange}
                      onChange={(event) =>
                        handleSaturdayChange("hoursRange", event.target.value)
                      }
                    />
                  </label>
                  <label className="edit-field">
                    <span>Note</span>
                    <textarea
                      rows={3}
                      value={saturdayDraft.note}
                      onChange={(event) =>
                        handleSaturdayChange("note", event.target.value)
                      }
                    />
                  </label>
                  <div className="edit-actions">
                    <button
                      className="edit-save"
                      type="button"
                      onClick={handleSaturdaySave}
                    >
                      Save
                    </button>
                    <button
                      className="edit-cancel"
                      type="button"
                      onClick={handleSaturdayCancel}
                    >
                      Cancel
                    </button>
                  </div>
                </div>
              )}
            </div>
          </div>
        </section>

        <section className="splash-band safety-section" aria-labelledby="safety-heading">
          <div className="band-inner">
            <div className="section-head" data-reveal="">
              <h2 id="safety-heading" className="section-title">
                Shop Essentials{" "}
                <span className="safety-accent">*SAFETY FIRST*</span>
              </h2>
              <p className="section-note">
                Everyone in the shop meets all four requirements before
                entering.
              </p>
            </div>

            <ul className="requirements">
              {REQUIREMENTS.map((requirement) => (
                <li className="requirement" key={requirement.label} data-reveal="">
                  {requirement.icon === "pants" ? (
                    <svg className="pants-icon" viewBox="0 0 24 24" aria-hidden="true" focusable="false">
                      <path fill="currentColor" d="M4 0h16l2 24h-8l-2-15-2 15H2L4 0Z" />
                    </svg>
                  ) : <i
                    className={`fas ${requirement.icon}`}
                    aria-hidden="true"
                  ></i>}
                  <span>{requirement.label}</span>
                </li>
              ))}
            </ul>

            <div className="safety-warning" role="note" data-reveal="">
              <i className="fas fa-exclamation-triangle" aria-hidden="true"></i>
              <p>
                Unsafe behavior will result in removal and suspension until
                cleared by the shop manager.
              </p>
            </div>
          </div>
        </section>
      </main>

      <footer className="splash-footer">
        <p>Copyright © 2025 GCU Engineering Shop</p>
        <p>
          <i className="fas fa-envelope" aria-hidden="true"></i>{" "}
          <a href="mailto:Paul.Lambertson@gcu.edu">Paul.Lambertson@gcu.edu</a>
        </p>
      </footer>
    </div>
  );
};

export default Splash;
